import React, { useState } from 'react';
import { Popover } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

/**
 * LR-700: the interaction shell shared by the summary bar's filter pills
 * (Settlement Ccy — LR-474, Term — LR-700). Extracted from CurrencyFilterPill so
 * the two pills cannot drift apart: the selection semantics below are subtle
 * enough that a second hand-written copy would be a standing bug (LR-698 is this
 * repo's fresh proof of what a duplicated invariant costs).
 *
 * What lives HERE: the button + popover, the checkbox/name rows, Select all /
 * Clear, and the selection algebra. What stays in each pill: its own data
 * (aggregate, counts), its trailing column, its footer, and its labels.
 *
 * Interaction model (LR-474 WP4, unchanged by the extraction):
 *   - clicking an entry NAME isolates it (only that value); clicking the name of
 *     the already-isolated value returns to All;
 *   - clicking the CHECKBOX toggles membership (from All, unchecking one value
 *     selects "all except it"); toggling the selection back to the full set — or
 *     unchecking the last remaining value — normalises to All (`[]`);
 *   - header `Select all` returns to All; `Clear` unchecks every box as a
 *     TRANSIENT draft (the filter is untouched until the next pick), so the user
 *     can rebuild a small selection from zero. The draft lives in the panel,
 *     which unmounts — and so resets — whenever the popover closes.
 * Store semantics: `selected = []` ⇔ All.
 */
export interface FilterPillEntry {
  /** Filter value as stored (what `onChange` emits). */
  key: string;
  /** What the row shows; defaults to `key`. */
  label?: string;
  count: number;
  /** Right-hand column (the ccy pill's money figure); omitted → nothing. */
  trailing?: React.ReactNode;
}

export interface FilterPillProps {
  /** Pill caption, e.g. "Settlement Ccy:" / "Term:". */
  caption: string;
  /** Collapsed-state label: "All", the isolated value, "2 of 3"… */
  buttonLabel: string;
  entries: FilterPillEntry[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** Tooltip on the pill button. */
  title: string;
  /** Row tooltip/aria: what clicking the NAME does. */
  isolateTitle: (label: string, isolated: boolean) => string;
  /** Shown instead of the list when `entries` is empty. */
  emptyMessage: string;
  /** Optional block under the list (the ccy pill's Σ≈ roll-up). Receives the
   *  draft flag so it can explain itself while every box is unchecked. */
  footer?: (opts: { draftCleared: boolean }) => React.ReactNode;
  className?: string;
  panelClassName?: string;
}

export const FilterPill: React.FC<FilterPillProps> = ({
  caption,
  buttonLabel,
  entries,
  selected,
  onChange,
  title,
  isolateTitle,
  emptyMessage,
  footer,
  className,
  panelClassName,
}) => {
  // A non-default selection (anything other than "All") is signalled by a blue
  // outline on the pill itself — the filter's UI lives here, NOT in the applied-
  // filters strip, so the pill carries its own active-state affordance.
  const isActive = selected.length > 0;

  return (
    <Popover className={clsx('relative', className)}>
      <Popover.Button
        className={clsx(
          'flex items-center gap-1 whitespace-nowrap rounded border bg-white px-2 py-1 text-xs focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500',
          isActive
            ? 'border-blue-500 ring-1 ring-blue-500 text-blue-700 hover:bg-blue-50'
            : 'border-slate-300 text-slate-600 hover:bg-slate-50',
        )}
        title={title}
      >
        <span className={isActive ? 'text-blue-500' : 'text-slate-400'}>{caption}</span>
        <span className={clsx('font-medium', isActive ? 'text-blue-700' : 'text-slate-700')}>
          {buttonLabel}
        </span>
        <ChevronDownIcon
          className={clsx('h-3.5 w-3.5', isActive ? 'text-blue-500' : 'text-slate-400')}
          aria-hidden="true"
        />
      </Popover.Button>
      <Popover.Panel
        className={clsx(
          'absolute right-0 z-20 mt-1 rounded-md border border-slate-200 bg-white py-1 shadow-lg',
          panelClassName ?? 'w-72',
        )}
      >
        <PillPanel
          entries={entries}
          selected={selected}
          onChange={onChange}
          isolateTitle={isolateTitle}
          emptyMessage={emptyMessage}
          footer={footer}
        />
      </Popover.Panel>
    </Popover>
  );
};

const PillPanel: React.FC<{
  entries: FilterPillEntry[];
  selected: string[];
  onChange: (next: string[]) => void;
  isolateTitle: (label: string, isolated: boolean) => string;
  emptyMessage: string;
  footer?: (opts: { draftCleared: boolean }) => React.ReactNode;
}> = ({ entries, selected, onChange, isolateTitle, emptyMessage, footer }) => {
  // `Clear` drafts an all-unchecked state WITHOUT touching the filter (the store
  // cannot represent "none" — [] means All). The next checkbox/name pick starts
  // a fresh selection; closing the popover discards the draft.
  const [draftCleared, setDraftCleared] = useState(false);

  if (entries.length === 0) {
    return <div className="px-3 py-2 text-xs text-slate-400">{emptyMessage}</div>;
  }

  const allKeys = entries.map((e) => e.key);
  const isAll = selected.length === 0;

  const isChecked = (key: string): boolean => !draftCleared && (isAll || selected.includes(key));
  const isIsolated = (key: string): boolean =>
    !draftCleared && selected.length === 1 && selected[0] === key;

  /** Normalise: full set ⇔ [] (All); empty result also collapses to All. */
  const commit = (next: string[]) => {
    setDraftCleared(false);
    if (next.length === 0 || allKeys.every((k) => next.includes(k))) {
      onChange([]);
    } else {
      onChange(next);
    }
  };

  const handleNameClick = (key: string) => {
    // Isolate; clicking the already-isolated name returns to All.
    commit(isIsolated(key) ? [] : [key]);
  };

  const handleCheckboxClick = (key: string) => {
    if (draftCleared) {
      commit([key]);
      return;
    }
    const effective = isAll ? allKeys : selected;
    const next = effective.includes(key)
      ? effective.filter((k) => k !== key)
      : [...effective, key];
    commit(next);
  };

  return (
    <>
      <div className="flex items-center gap-3 border-b border-slate-200 px-3 py-1.5">
        <button
          type="button"
          onClick={() => commit([])}
          className="text-xs font-medium text-blue-600 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
        >
          Select all
        </button>
        <button
          type="button"
          onClick={() => setDraftCleared(true)}
          className="text-xs font-medium text-slate-500 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
          title="Uncheck all — pick values one by one (closing without picking keeps the current filter)"
        >
          Clear
        </button>
      </div>
      <ul className="max-h-64 overflow-auto">
        {entries.map(({ key, label, count, trailing }) => {
          const text = label ?? key;
          const checked = isChecked(key);
          const isolated = isIsolated(key);
          return (
            <li key={key} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-slate-50">
              {/* Checkbox = membership toggle. A real <button> with checkbox
                  semantics (role=checkbox + aria-checked) — role=checkbox needs
                  no required-context parent (unlike menuitemcheckbox). */}
              {/* 24×24 hit target (WCAG 2.5.8) around the 14px visual box. */}
              <button
                type="button"
                role="checkbox"
                aria-checked={checked}
                aria-label={`${text} — include in filter`}
                onClick={() => handleCheckboxClick(key)}
                className="-my-1 flex h-6 w-6 shrink-0 items-center justify-center rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
              >
                <span
                  aria-hidden="true"
                  className={clsx(
                    'flex h-3.5 w-3.5 items-center justify-center rounded border',
                    checked ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-300 bg-white',
                  )}
                >
                  {checked && (
                    <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M2.5 6.5l2.5 2.5 4.5-5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
              </button>
              {/* Name = isolation toggle ("only this value" / back to All). */}
              <button
                type="button"
                onClick={() => handleNameClick(key)}
                title={isolateTitle(text, isolated)}
                aria-label={`${text}, ${count} trades — ${isolateTitle(text, isolated).toLowerCase()}`}
                className="flex flex-1 items-center gap-2 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
              >
                <span className="w-20 font-medium text-slate-700">{text}</span>
                <span className="w-10 tabular-nums text-slate-400">({count})</span>
                {trailing != null && (
                  <span className="ml-auto font-mono tabular-nums text-slate-900">{trailing}</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      {footer && (
        <div className="mt-1 flex items-center justify-between border-t border-slate-200 px-3 py-1.5">
          {footer({ draftCleared })}
        </div>
      )}
    </>
  );
};
